<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import { useSectorPermissions } from 'dashboard/composables/useSectorPermissions';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentIdentifier = ref('');
const selectedRoleId = ref('agent');
const selectedSectorId = ref(null);
const selectedTeams = ref([]);

const isSectorRole = computed(() =>
  ['supervisor', 'sector_admin', 'agent'].includes(selectedRoleId.value)
);

const rules = computed(() => ({
  agentName: { required },
  agentIdentifier: { required },
  selectedRoleId: { required },
  selectedSectorId: { required: requiredIf(() => isSectorRole.value) },
}));

const v$ = useVuelidate(rules, {
  agentName,
  agentIdentifier,
  selectedRoleId,
  selectedSectorId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const getSectors = useMapGetter('sectors/getSectors');
const getTeams = useMapGetter('teams/getTeams');

const { isAccountAdmin, isSectorAdmin, hasSectorMembership } =
  useSectorPermissions();

const roles = computed(() => {
  // account-level roles only for administrators
  const accountRoles = isAccountAdmin.value
    ? [
        {
          id: 'administrator',
          name: 'administrator',
          label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
        {
          id: 'agent',
          name: 'agent',
          label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
        },
      ]
    : [];

  // sector_admin can be assigned by account admins or sector_admins
  const sectorAdminRole =
    isAccountAdmin.value || isSectorAdmin.value
      ? [
          {
            id: 'sector_admin',
            name: 'sector_admin',
            label: t('AGENT_MGMT.AGENT_TYPES.SECTOR_ADMIN'),
          },
        ]
      : [];

  // supervisor can be assigned by anyone with sector membership or account admin
  // they can also assign 'agent' role within the sector
  const supervisorRole =
    isAccountAdmin.value || hasSectorMembership.value
      ? [
          {
            id: 'supervisor',
            name: 'supervisor',
            label: t('AGENT_MGMT.AGENT_TYPES.SUPERVISOR'),
          },
          {
            id: 'agent',
            name: 'agent',
            label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
          },
        ]
      : [];

  const customRoles = isAccountAdmin.value
    ? getCustomRoles.value.map(role => ({
        id: role.id,
        name: `custom_${role.id}`,
        label: role.name,
      }))
    : [];

  return [...accountRoles, ...sectorAdminRole, ...supervisorRole, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const availableTeams = computed(() => {
  if (!isSectorRole.value || !selectedSectorId.value) {
    return getTeams.value;
  }

  return getTeams.value.filter(team => team.sector_id === selectedSectorId.value);
});

watch([isSectorRole, selectedSectorId, getTeams], () => {
  const allowedTeamIds = new Set(availableTeams.value.map(team => team.id));
  selectedTeams.value = selectedTeams.value.filter(team =>
    allowedTeamIds.has(team.id)
  );
});

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const identifier = agentIdentifier.value.trim();

    const payload = {
      name: agentName.value,
      ...(identifier.includes('@')
        ? { email: identifier }
        : { username: identifier }),
      team_ids: selectedTeams.value.map(t => t.id),
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else if (isSectorRole.value) {
      payload.role = 'agent';
      payload.role_in_sector = selectedRole.value.name;
      payload.sector_id = selectedSectorId.value;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="isSectorRole" class="w-full">
        <label :class="{ error: v$.selectedSectorId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.SECTOR.LABEL') }}
          <select v-model="selectedSectorId" @change="v$.selectedSectorId.$touch">
            <option :value="null" disabled>
              {{ $t('AGENT_MGMT.ADD.FORM.SECTOR.PLACEHOLDER') }}
            </option>
            <option
              v-for="sector in getSectors"
              :key="sector.id"
              :value="sector.id"
            >
              {{ sector.name }}
            </option>
          </select>
          <span v-if="v$.selectedSectorId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.SECTOR.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentIdentifier.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.IDENTIFIER.LABEL') }}
          <input
            v-model="agentIdentifier"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.IDENTIFIER.PLACEHOLDER')"
            @input="v$.agentIdentifier.$touch"
          />
        </label>
      </div>

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('AGENT_MGMT.ADD.FORM.TEAMS.LABEL') }}
        </label>
        <multiselect
          v-model="selectedTeams"
          :options="availableTeams"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="$t('AGENT_MGMT.ADD.FORM.TEAMS.PLACEHOLDER')"
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        >
          <template #noOptions>{{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}</template>
        </multiselect>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
